import { useState } from "react"
import { useNavigate } from "react-router-dom"

import {
  emailVerificationCore,
  EmailVerificationResult,
  startVerificationPolling,
} from "@fuse/services/emailVerificationService"
import {
  showErrorToast,
  showSuccessToast,
} from "@fuse/sharedComponents/themeToast/toastUtils"
import { useRecoilState } from "recoil"

import { defaultUser, userState } from "@fuse/recoil/signup"

export interface EmailVerificationCallbacks {
  onSuccess?: () => void
  onCloseUI?: () => void
  onRequireInboxCheck?: () => void
  onVerificationComplete?: () => void
}

export interface UseEmailVerificationReturn {
  verifyEmail: (
    email: string,
    callbacks?: EmailVerificationCallbacks,
  ) => Promise<EmailVerificationResult>
  isLoading: boolean
  startPolling: (onVerified: () => void) => () => void
}

/**
 * React hook wrapper for email verification
 * Provides convenient API for React components with automatic state management
 */
export const useEmailVerification = (): UseEmailVerificationReturn => {
  const [user, setUser] = useRecoilState(userState)
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()

  const verifyEmail = async (
    email: string,
    callbacks?: EmailVerificationCallbacks,
  ): Promise<EmailVerificationResult> => {
    setIsLoading(true)

    try {
      const result = await emailVerificationCore({
        email,
        updateUserState: setUser,
        showToast: {
          success: showSuccessToast,
          error: showErrorToast,
        },
        onSignOutRequired: () => {
          setUser(defaultUser)
          navigate("/")
        },
      })

      if (result.success) {
        // Handle success callbacks
        callbacks?.onSuccess?.()

        if (result.isDevMode) {
          // Dev mode: immediate verification, close UI
          // callbacks?.onCloseUI?.()
          callbacks?.onVerificationComplete?.()
        } else if (result.requiresInboxCheck) {
          // Production mode: show inbox check UI
          callbacks?.onRequireInboxCheck?.()
        }
      }

      return result
    } finally {
      setIsLoading(false)
    }
  }

  const startPolling = (onVerified: () => void): (() => void) => {
    const handleVerificationComplete = () => {
      showSuccessToast("Email verified successfully!")
      onVerified()
    }

    return startVerificationPolling(handleVerificationComplete)
  }

  return {
    verifyEmail,
    isLoading,
    startPolling,
  }
}
